import { apiFetch } from '@/lib/api-client';
import type {
  CreateAddressRequest,
  CreateAddressResult,
  AddressRefOption,
  FiberCascadeOption,
  NeighborDefaultsResult,
  ReleaseNeighborsResult,
  BuildingBoxesResponse,
  AddBoxesRequest,
  AddBoxesResult,
  GeocodeAddressResult,
} from '@uniticket/shared';

// apiFetch wrappers for the Create-address feature (no react-query — same style as useAddressValidation).
// Internal-only endpoints (gated server-side to Unifiber staff); the JWT is attached by apiFetch.

/** Create a new address on the LOCAL writable copy. Server refuses (409) unless ADDR_TABLE_TARGET=local.
 *  `viaConsole` posts to the ADMIN-only DMP-console route instead — same body, same service, but no
 *  ticket is involved and the action log records the console as the source. */
export async function createAddress(body: CreateAddressRequest, viaConsole = false) {
  return apiFetch<{ data: CreateAddressResult }>(viaConsole ? '/dmp/console/addresses' : '/addresses', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ── "Add a box" — existing boxes + suggested bus_numbers for a building, and the add call ──
/** Existing boxes of a building + suggested next bus_numbers (for the Add-a-box form). */
export async function fetchBuildingBoxes(params: {
  buildingId?: number | null;
  jvid?: number | null;
  count?: number;
}): Promise<BuildingBoxesResponse> {
  const sp = new URLSearchParams();
  if (params.buildingId != null) sp.set('buildingId', String(params.buildingId));
  if (params.jvid != null) sp.set('jvid', String(params.jvid));
  if (params.count != null) sp.set('count', String(params.count));
  const q = sp.toString();
  const res = await apiFetch<{ data: BuildingBoxesResponse }>(`/addresses/building/boxes${q ? `?${q}` : ''}`);
  return res.data;
}

/** Add boxes (bus_numbers) to a building on the LOCAL copy. Server refuses (409) unless ADDR_TABLE_TARGET=local.
 *  `viaConsole` posts to the ADMIN-only DMP-console route (see createAddress). */
export async function addBoxes(body: AddBoxesRequest, viaConsole = false) {
  return apiFetch<{ data: AddBoxesResult }>(viaConsole ? '/dmp/console/addresses/boxes' : '/addresses/boxes', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Convert an SDU address into an MDU building (local copy only). `boxes` = the FULL set of bus_numbers
 *  (≥2); the original row becomes boxes[0]. Server refuses (409) unless local / when already MDU.
 *  Depuis C0.5 la route applique la garde de capacité d'un ajout de boîte : mêmes deux champs, mêmes refus. */
export async function convertToMdu(body: {
  jvid: number; boxes: string[]; inScope?: boolean; ticketNumber?: string | null;
  unitPosition?: AddBoxesRequest['unitPosition']; capacityOverride?: AddBoxesRequest['capacityOverride'];
}) {
  return apiFetch<{ data: AddBoxesResult }>('/addresses/convert-to-mdu', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

function qs(params: Record<string, string | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') sp.set(k, v);
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// ── Address referential dropdowns (DMP read-only, local fallback server-side) → ComboOption-shaped ──
async function refSearch(path: string, params: Record<string, string | undefined>): Promise<AddressRefOption[]> {
  const res = await apiFetch<{ data: AddressRefOption[] }>(`/addresses/ref/${path}${qs(params)}`);
  return res.data;
}

/** Chosen ancestor values for the strict escalating cascade municipality → city → postal code → street. */
export interface AddressRefFilters {
  municipality?: string;
  city?: string;
  postalCode?: string;
}

export const searchMunicipalities = (q: string) => refSearch('municipalities', { q });
export const searchCities = (q: string, f?: AddressRefFilters) =>
  refSearch('cities', { q, municipality: f?.municipality });
export const searchPostalCodes = (q: string, f?: AddressRefFilters) =>
  refSearch('postal-codes', { q, municipality: f?.municipality, city: f?.city });
export const searchStreets = (q: string, f?: AddressRefFilters) =>
  refSearch('streets', { q, municipality: f?.municipality, city: f?.city, postalCode: f?.postalCode });
export const searchAreas = (q: string) => refSearch('areas', { q });

// ── "Check the neighbour" — pre-fill area + delivery_status from the nearest same-parity neighbour ──
export interface NeighborQuery {
  street: string;
  houseNumber: string;
  postalCode?: string;
  city?: string;
  municipality?: string;
}

export async function findNeighbor(query: NeighborQuery): Promise<NeighborDefaultsResult> {
  const res = await apiFetch<{ data: NeighborDefaultsResult }>(`/addresses/neighbor${qs({ ...query })}`);
  return res.data;
}

/** Les voisins de même parité, en dessous et au-dessus, et combien de leurs logements sont released — pour
 *  l'avertissement du « Release immédiatement » (demande du user, 17-09). */
export async function fetchReleaseNeighbors(query: NeighborQuery): Promise<ReleaseNeighborsResult> {
  const res = await apiFetch<{ data: ReleaseNeighborsResult }>(`/addresses/release-neighbors${qs({ ...query })}`);
  return res.data;
}

// ── PoP dropdown (real DMP read-only server-side) → {id, text} for the "Create fake ODF" toggle ──
// The only fiber dropdown left: the operator picks a PoP, its rack/panel/port slot is resolved server-side.
export const searchPops = async (q: string): Promise<FiberCascadeOption[]> => {
  const res = await apiFetch<{ data: FiberCascadeOption[] }>(`/fibers/pops${qs({ q })}`);
  return res.data;
};

// ── ICAR geocoding — Lambert-72 X/Y for the Create-address coordinates (best-effort) ──
export interface GeocodeQuery {
  street: string;
  house: string;
  postalCode?: string;
  city?: string;
  municipality?: string;
}

export async function geocodeAddress(query: GeocodeQuery): Promise<GeocodeAddressResult> {
  const res = await apiFetch<{ data: GeocodeAddressResult }>(`/addresses/geocode${qs({ ...query })}`);
  return res.data;
}
